import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useLegalAdmin, errText } from "./useLegalAdmin";
import { LegalPagesManager } from "./LegalPagesManager";

const INPUT = "w-full border-2 border-black px-3 py-2 text-sm mt-1 bg-white disabled:bg-zinc-50";
const FIELDS = [
  ["legal_name", "Legal name shown in disclaimers", "e.g. Example Law, LLC DBA Example Injury Firm"],
  ["responsible_attorney", "Attorney responsible for the ads", "Many states require this name"],
  ["office_location", "Principal office (city, state)", "e.g. Dallas, TX"],
];

function BuyerLegalDetails({ buyer, canEdit, onSaved }) {
  const [form, setForm] = useState(buyer);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(buyer); }, [buyer]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { legal_name, office_location, responsible_attorney, show_on_partners } = form;
      await api.put(`/admin/legal/buyer/${buyer.id}`, { legal_name, office_location, responsible_attorney, show_on_partners });
      toast.success("Legal details saved");
      onSaved();
    } catch (e) { toast.error(errText(e, "Couldn't save")); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="buyer-legal-details">
      {FIELDS.map(([k, label, ph]) => (
        <label key={k} className="block">
          <span className="label-mono">{label}</span>
          <input className={INPUT} value={form[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} disabled={!canEdit} data-testid={`buyer-legal-${k}`} />
        </label>
      ))}
      <label className="flex items-center gap-2 text-xs md:col-span-2">
        <input type="checkbox" checked={form.show_on_partners} onChange={(e) => set("show_on_partners", e.target.checked)} disabled={!canEdit} data-testid="buyer-legal-show-on-partners" />
        List this buyer on the public Legal Partners page once they're live
      </label>
      {canEdit && (
        <button type="button" onClick={save} disabled={busy} data-testid="buyer-legal-save"
                className="justify-self-end border-2 border-black bg-black text-white px-3 py-2 text-[10px] uppercase tracking-widest disabled:opacity-40">
          {busy ? "Saving…" : "Save details"}
        </button>
      )}
    </div>
  );
}

export const BuyerLegalDialog = ({ pageId, onClose, onSaved }) => {
  const { data, error, reload } = useLegalAdmin(pageId);
  const saved = () => { reload(); onSaved?.(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="buyer-legal-dialog">
      <div className="brut-card bg-white w-full max-w-4xl my-8">
        <div className="px-4 py-3 border-b-2 border-black flex items-center gap-3">
          <div className="font-chivo font-black uppercase tracking-tighter">Legal pages · {data?.buyer?.firm_name || "Buyer"}</div>
          <button type="button" className="ml-auto" onClick={onClose} data-testid="buyer-legal-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-4 flex flex-col gap-5">
          {error && <div className="text-xs text-red-700" data-testid="buyer-legal-error">Couldn't load this buyer's legal settings.</div>}
          {!data && !error && <div className="label-mono">Loading…</div>}
          {data && (
            <>
              <BuyerLegalDetails buyer={data.buyer} canEdit={data.can_edit} onSaved={saved} />
              <div className="border-t-2 border-black pt-4">
                <div className="label-mono mb-2">Pages shown on this buyer's links (?b={data.buyer.code})</div>
                <LegalPagesManager data={data} pageId={pageId} onSaved={saved} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
